import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Collapse,
  Theme,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import {
  ExpandMore,
  ExpandLess,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
} from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { EditorState, ContentState, Modifier, CompositeDecorator, ContentBlock, convertFromHTML } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { FormType, AvailableFormField, FormWizardProgram, FormWizardActivity } from './form-responses-types';
import { FormNotificationSettings } from './FormNotificationSettings';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    settingsSection: {
      background: 'white',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      padding: '20px 24px',
      marginBottom: '16px',
    },
    settingsSectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer',
    },
    settingsSectionHeaderExpanded: {
      paddingBottom: '16px',
      borderBottom: '1px solid #efefef',
      marginBottom: '16px',
    },
    settingsSectionInfo: {
      flex: 1,
    },
    settingsSectionTitle: {
      fontSize: '16px',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    settingsSectionDescription: {
      fontSize: '14px',
      color: '#808080',
      lineHeight: '1.4',
      marginTop: '4px',
    },
    settingsExpandIcon: {
      padding: '4px',
      color: '#808080',
    },
    sectionTitle: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: theme.color.grey.neutral400,
      letterSpacing: '-0.01em',
    },
    responseCard: {
      backgroundColor: '#fff',
      border: `1px solid ${theme.color.grey.neutralBrand200}`,
      borderRadius: '8px',
      padding: '24px',
      marginBottom: '16px',
    },
  })
);

interface SettingsTabProps {
  formId: string;
  form: FormType;
  fieldMappings: Record<string, string>;
  availableFormFields: AvailableFormField[];
  programs: FormWizardProgram[];
  allActivities: FormWizardActivity[];
  activitiesForProgram: FormWizardActivity[];
  sessionsForActivity: any[];
  genericSessionCount: number;
  savingMappings: boolean;
  onFieldMappingsChange: (mappings: Record<string, string>) => void;
  onSaveFieldMappings: () => void;
  onGenericSessionCountChange: (count: number | ((prev: number) => number)) => void;
  onRefetchForms: () => void;
}

const activityFields = [
  { key: 'activity.activityName', label: 'Activity Name', type: 'string' },
  { key: 'activity.activityDescription', label: 'Activity Description', type: 'string' },
  { key: 'activity.startDate', label: 'Start Date', type: 'date' },
  { key: 'activity.endDate', label: 'End Date', type: 'date' },
  { key: 'activity.location.formatted', label: 'Location Address', type: 'string' },
  { key: 'activity.location.description', label: 'Location Description', type: 'string' },
  { key: 'activity.remoteLocation.details', label: 'Remote Location Details', type: 'string' },
  { key: 'activity.maxTeam', label: 'Max Team Size', type: 'number' },
];

const rosterTemplateFields = [
  { key: 'rosterTemplate.rosterName', label: 'Custom Roster Name', type: 'string' },
  { key: 'rosterTemplate.location', label: 'Location', type: 'string' },
];

const RosterNotesTemplateEditor: React.FC<{
  value: string;
  onChange: (html: string) => void;
  availableFormFields: AvailableFormField[];
}> = ({ value, onChange, availableFormFields }) => {
  const { theme } = useCampfireTheme();
  const fieldsRef = useRef<AvailableFormField[]>(availableFormFields);
  fieldsRef.current = availableFormFields;

  const [decorator] = useState(() => {
    const mergeTagStrategy = (contentBlock: ContentBlock, callback: (start: number, end: number) => void) => {
      const text = contentBlock.getText();
      const regex = /\{\{[^}]+\}\}/g;
      let match = regex.exec(text);
      while (match !== null) {
        callback(match.index, match.index + match[0].length);
        match = regex.exec(text);
      }
    };

    const MergeTagChip = (props: any) => {
      const { decoratedText, children } = props;
      const fieldId = (decoratedText || '').replace(/[{}]/g, '').trim();
      const fieldName = fieldsRef.current.find((f) => f.taskFieldId === fieldId)?.name || fieldId;
      return (
        <span
          style={{
            position: 'relative',
            backgroundColor: '#e3f2fd',
            borderRadius: 4,
            display: 'inline',
          }}
        >
          <span style={{ fontSize: 0, lineHeight: 0, width: 0, overflow: 'hidden', display: 'inline-block' }}>
            {children}
          </span>
          <span
            contentEditable={false}
            suppressContentEditableWarning
            style={{
              padding: '2px 6px',
              color: '#1565c0',
              fontWeight: 500,
              userSelect: 'none',
              fontSize: '0.85em',
            }}
          >
            {fieldName}
          </span>
        </span>
      );
    };

    return new CompositeDecorator([{ strategy: mergeTagStrategy, component: MergeTagChip }]);
  });

  const [editorState, setEditorState] = useState(() => {
    if (value && (value.includes('{{') || value.startsWith('<'))) {
      try {
        const blocksFromHTML = convertFromHTML(value);
        if (blocksFromHTML.contentBlocks) {
          const content = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
          return EditorState.createWithContent(content, decorator);
        }
      } catch {
        // Fall through to empty state
      }
    }
    return EditorState.createEmpty(decorator);
  });

  const handleEditorChange = (newState: EditorState) => {
    setEditorState(newState);
    const hasContent = newState.getCurrentContent().hasText();
    const html = hasContent ? stateToHTML(newState.getCurrentContent()) : '';
    onChange(html);
  };

  const insertFieldTag = (taskFieldId: string) => {
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();
    const newContent = Modifier.insertText(contentState, selectionState, `{{${taskFieldId}}}`);
    const newState = EditorState.push(editorState, newContent, 'insert-characters');
    handleEditorChange(newState);
  };

  return (
    <Box style={{ marginTop: 16 }}>
      <Box display='flex' alignItems='center' style={{ marginBottom: 8 }}>
        <Typography style={{ fontSize: '0.875rem', fontWeight: 600, color: theme.color.grey.neutral400 }}>
          Roster Notes Template
        </Typography>
        <FormControl size='small' variant='outlined' style={{ marginLeft: 'auto', minWidth: 160 }}>
          <Select
            displayEmpty
            value=''
            onChange={(e) => {
              if (e.target.value) insertFieldTag(String(e.target.value));
            }}
            style={{ fontSize: '0.8rem' }}
          >
            <MenuItem value='' disabled>
              Insert Field...
            </MenuItem>
            {availableFormFields.map((f) => (
              <MenuItem key={f.taskFieldId} value={f.taskFieldId}>
                {f.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box
        style={{
          border: '1px solid #e0e0e0',
          borderRadius: 4,
          padding: '8px 12px',
          minHeight: 80,
        }}
      >
        <CampfireRichEditor
          editorState={editorState}
          setEditorState={handleEditorChange}
          placeholder='Compose roster notes template with merge tags...'
        />
      </Box>
      <Typography style={{ fontSize: '0.75rem', color: '#999', marginTop: 4 }}>
        Use the &quot;Insert Field&quot; dropdown to add form field values. Format text using the toolbar.
      </Typography>
    </Box>
  );
};

const SessionMappingRow: React.FC<{
  sessionKey: string;
  sessionLabel: string;
  fieldMappings: Record<string, string>;
  availableFormFields: AvailableFormField[];
  onFieldMappingsChange: (mappings: Record<string, string>) => void;
  onRemove?: () => void;
}> = ({ sessionKey, sessionLabel, fieldMappings, availableFormFields, onFieldMappingsChange, onRemove }) => {
  const { theme } = useCampfireTheme();

  return (
    <Box style={{ marginBottom: '24px' }}>
      <Box display='flex' alignItems='center' style={{ marginBottom: '12px' }}>
        <Typography style={{ fontSize: '0.9rem', fontWeight: 600, color: theme.color.grey.neutral400 }}>
          {sessionLabel}
        </Typography>
        {onRemove && (
          <TabletButton
            size='small'
            variant='outlined'
            style={{
              marginLeft: '8px',
              textTransform: 'none',
              padding: '4px 12px',
              borderColor: theme.color.grey.neutral200,
              color: theme.color.grey.neutral400,
            }}
            onClick={onRemove}
          >
            Remove
          </TabletButton>
        )}
      </Box>
      <Grid container spacing={2}>
        {['customName', 'startTime', 'endTime'].map((fieldName) => (
          <Grid item xs={12} sm={4} key={fieldName}>
            <FormControl fullWidth variant='outlined' size='small'>
              <InputLabel>
                {fieldName === 'customName' ? 'Custom Name' : fieldName === 'startTime' ? 'Start Time' : 'End Time'}
              </InputLabel>
              <Select
                value={fieldMappings[`${sessionKey}.${fieldName}`] || ''}
                onChange={(e) =>
                  onFieldMappingsChange({
                    ...fieldMappings,
                    [`${sessionKey}.${fieldName}`]: String(e.target.value),
                  })
                }
                label={
                  fieldName === 'customName' ? 'Custom Name' : fieldName === 'startTime' ? 'Start Time' : 'End Time'
                }
              >
                <MenuItem value=''>
                  <em>No mapping</em>
                </MenuItem>
                {availableFormFields.map((formField) => (
                  <MenuItem key={formField.taskFieldId} value={formField.taskFieldId}>
                    {formField.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export const SettingsTab: React.FC<SettingsTabProps> = ({
  formId,
  form,
  fieldMappings,
  availableFormFields,
  programs,
  activitiesForProgram,
  sessionsForActivity,
  genericSessionCount,
  savingMappings,
  onFieldMappingsChange,
  onSaveFieldMappings,
  onGenericSessionCountChange,
  onRefetchForms,
}) => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const [wizardSettingsExpanded, setWizardSettingsExpanded] = useState(false);
  const [notificationsExpanded, setNotificationsExpanded] = useState(false);

  return (
    <Box>
      {/* Form Wizard Section */}
      <Box className={classes.settingsSection}>
        <Box
          className={`${classes.settingsSectionHeader} ${
            wizardSettingsExpanded ? classes.settingsSectionHeaderExpanded : ''
          }`}
          onClick={() => setWizardSettingsExpanded(!wizardSettingsExpanded)}
        >
          <Box className={classes.settingsSectionInfo}>
            <Typography className={classes.settingsSectionTitle}>
              <SettingsIcon style={{ fontSize: '18px' }} /> Form Wizard
            </Typography>
            <Typography className={classes.settingsSectionDescription}>
              Configure default entity types and field mappings for the Form Wizard
            </Typography>
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {wizardSettingsExpanded && (
              <Box onClick={(e) => e.stopPropagation()}>
                <TabletButton
                  variant='contained'
                  color='primary'
                  onClick={onSaveFieldMappings}
                  disabled={savingMappings}
                  style={{
                    backgroundColor: theme.color.secondary.main900,
                    color: '#ffffff',
                    boxSizing: 'border-box',
                    borderRadius: '4px',
                    textTransform: 'none',
                    padding: '7px 12px',
                    minWidth: '40px',
                  }}
                >
                  {savingMappings ? 'Saving...' : 'Save'}
                </TabletButton>
              </Box>
            )}
            <IconButton size='small' className={classes.settingsExpandIcon}>
              {wizardSettingsExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        </Box>
        <Collapse in={wizardSettingsExpanded}>
          <Box className={classes.responseCard} style={{ marginBottom: '24px' }}>
            <Typography className={classes.sectionTitle} style={{ marginBottom: '16px' }}>
              Default Entity Type
            </Typography>
            <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300, marginBottom: '16px' }}>
              Choose which entity type is pre-selected when opening the Form Wizard.
            </Typography>
            <FormControl variant='outlined' size='small' style={{ minWidth: 200 }}>
              <InputLabel>Default Type</InputLabel>
              <Select
                value={fieldMappings['wizard.defaultEntityType'] || ''}
                onChange={(e) =>
                  onFieldMappingsChange({ ...fieldMappings, 'wizard.defaultEntityType': String(e.target.value) })
                }
                label='Default Type'
              >
                <MenuItem value=''>
                  <em>No default</em>
                </MenuItem>
                <MenuItem value='activity'>Activity</MenuItem>
                <MenuItem value='roster_template'>Roster Template</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {fieldMappings['wizard.defaultEntityType'] && availableFormFields.length > 0 && (
            <>
              <Typography className={classes.sectionTitle} style={{ marginBottom: '16px' }}>
                Field Mappings
              </Typography>
              <Typography style={{ fontSize: '0.875rem', color: theme.color.grey.neutral300, marginBottom: '24px' }}>
                Configure how form submission fields map to{' '}
                {fieldMappings['wizard.defaultEntityType'] === 'activity' ? 'Activity' : 'Roster Template'} fields.
              </Typography>

              {fieldMappings['wizard.defaultEntityType'] === 'activity' && (
                <Box className={classes.responseCard}>
                  <Typography className={classes.sectionTitle} style={{ marginBottom: '16px' }}>
                    Activity Field Mappings
                  </Typography>
                  <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300, marginBottom: '16px' }}>
                    Map form fields to Activity properties. When creating an Activity from the Form Wizard, these values
                    will be pre-filled.
                  </Typography>
                  <Divider style={{ marginBottom: '16px' }} />
                  <Grid container spacing={2}>
                    {activityFields.map((field) => (
                      <Grid item xs={12} sm={6} key={field.key}>
                        <FormControl fullWidth variant='outlined' size='small'>
                          <InputLabel>{field.label}</InputLabel>
                          <Select
                            value={fieldMappings[field.key] || ''}
                            onChange={(e) =>
                              onFieldMappingsChange({ ...fieldMappings, [field.key]: String(e.target.value) })
                            }
                            label={field.label}
                          >
                            <MenuItem value=''>
                              <em>No mapping</em>
                            </MenuItem>
                            {availableFormFields.map((formField) => (
                              <MenuItem key={formField.taskFieldId} value={formField.taskFieldId}>
                                {formField.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {fieldMappings['wizard.defaultEntityType'] === 'roster_template' && (
                <>
                  <Box className={classes.responseCard}>
                    <Typography className={classes.sectionTitle} style={{ marginBottom: '16px' }}>
                      Default Program &amp; Activity
                    </Typography>
                    <Typography
                      style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300, marginBottom: '16px' }}
                    >
                      Select default program and activity for roster templates created from this form.
                    </Typography>
                    <Divider style={{ marginBottom: '16px' }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth variant='outlined' size='small'>
                          <InputLabel>Default Program</InputLabel>
                          <Select
                            value={fieldMappings['rosterTemplate.defaultProgramId'] || ''}
                            onChange={(e) => {
                              const newProgramId = String(e.target.value);
                              onFieldMappingsChange({
                                ...fieldMappings,
                                'rosterTemplate.defaultProgramId': newProgramId,
                                'rosterTemplate.defaultActivityId': '',
                              });
                            }}
                            label='Default Program'
                          >
                            <MenuItem value=''>
                              <em>No default</em>
                            </MenuItem>
                            {programs.map((program) => (
                              <MenuItem key={program.programId} value={program.programId}>
                                {program.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth variant='outlined' size='small'>
                          <InputLabel>Default Activity</InputLabel>
                          <Select
                            value={fieldMappings['rosterTemplate.defaultActivityId'] || ''}
                            onChange={(e) =>
                              onFieldMappingsChange({
                                ...fieldMappings,
                                'rosterTemplate.defaultActivityId': String(e.target.value),
                              })
                            }
                            label='Default Activity'
                          >
                            <MenuItem value=''>
                              <em>No default</em>
                            </MenuItem>
                            {activitiesForProgram.map((activity) => (
                              <MenuItem key={activity.activityId} value={activity.activityId}>
                                {activity.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box className={classes.responseCard}>
                    <Typography className={classes.sectionTitle} style={{ marginBottom: '16px' }}>
                      Roster Template Field Mappings
                    </Typography>
                    <Typography
                      style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300, marginBottom: '16px' }}
                    >
                      Map form fields to Roster Template properties. When creating a Roster Template from the Form
                      Wizard, these values will be pre-filled.
                    </Typography>
                    <Divider style={{ marginBottom: '16px' }} />
                    <Grid container spacing={2}>
                      {rosterTemplateFields.map((field) => (
                        <Grid item xs={12} sm={6} key={field.key}>
                          <FormControl fullWidth variant='outlined' size='small'>
                            <InputLabel>{field.label}</InputLabel>
                            <Select
                              value={fieldMappings[field.key] || ''}
                              onChange={(e) =>
                                onFieldMappingsChange({ ...fieldMappings, [field.key]: String(e.target.value) })
                              }
                              label={field.label}
                            >
                              <MenuItem value=''>
                                <em>No mapping</em>
                              </MenuItem>
                              {availableFormFields.map((formField) => (
                                <MenuItem key={formField.taskFieldId} value={formField.taskFieldId}>
                                  {formField.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      ))}
                    </Grid>
                    <RosterNotesTemplateEditor
                      value={fieldMappings['rosterTemplate.rosterNotes'] || ''}
                      onChange={(html) =>
                        onFieldMappingsChange({ ...fieldMappings, 'rosterTemplate.rosterNotes': html })
                      }
                      availableFormFields={availableFormFields}
                    />
                  </Box>

                  <Box className={classes.responseCard}>
                    <Typography className={classes.sectionTitle} style={{ marginBottom: '16px' }}>
                      Session Customization Mappings
                    </Typography>
                    <Typography
                      style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300, marginBottom: '16px' }}
                    >
                      {sessionsForActivity.length > 0
                        ? 'Map form fields to session properties for the selected activity.'
                        : "Map form fields to session properties by position. When creating a roster, these mappings will apply to the activity's sessions in order (1st mapping \u2192 1st session, etc)."}
                    </Typography>
                    <Divider style={{ marginBottom: '16px' }} />

                    {sessionsForActivity.length > 0 &&
                      sessionsForActivity.map((session: any) => (
                        <SessionMappingRow
                          key={session.sessionId}
                          sessionKey={`session.${session.sessionId}`}
                          sessionLabel={session.name}
                          fieldMappings={fieldMappings}
                          availableFormFields={availableFormFields}
                          onFieldMappingsChange={onFieldMappingsChange}
                        />
                      ))}

                    {sessionsForActivity.length === 0 && (
                      <>
                        {Array.from({ length: genericSessionCount }, (_unused, i) => i).map((sessionIndex) => (
                          <SessionMappingRow
                            key={sessionIndex}
                            sessionKey={`session.${sessionIndex}`}
                            sessionLabel={`Session ${sessionIndex + 1}`}
                            fieldMappings={fieldMappings}
                            availableFormFields={availableFormFields}
                            onFieldMappingsChange={onFieldMappingsChange}
                            onRemove={
                              sessionIndex > 0
                                ? () => {
                                    const newMappings = { ...fieldMappings };
                                    delete newMappings[`session.${sessionIndex}.customName`];
                                    delete newMappings[`session.${sessionIndex}.startTime`];
                                    delete newMappings[`session.${sessionIndex}.endTime`];
                                    onFieldMappingsChange(newMappings);
                                    onGenericSessionCountChange((prev: number) => prev - 1);
                                  }
                                : undefined
                            }
                          />
                        ))}
                        {genericSessionCount < 5 && (
                          <TabletButton
                            variant='outlined'
                            size='small'
                            style={{
                              textTransform: 'none',
                              padding: '6px 16px',
                              borderColor: theme.color.secondary.main900,
                              color: theme.color.secondary.main900,
                            }}
                            onClick={() => onGenericSessionCountChange((prev: number) => prev + 1)}
                          >
                            + Add Session
                          </TabletButton>
                        )}
                      </>
                    )}
                  </Box>
                </>
              )}
            </>
          )}
        </Collapse>
      </Box>

      {/* Notifications Section */}
      <Box className={classes.settingsSection}>
        <Box
          className={`${classes.settingsSectionHeader} ${
            notificationsExpanded ? classes.settingsSectionHeaderExpanded : ''
          }`}
          onClick={() => setNotificationsExpanded(!notificationsExpanded)}
        >
          <Box className={classes.settingsSectionInfo}>
            <Typography className={classes.settingsSectionTitle}>
              <NotificationsIcon style={{ fontSize: '18px' }} /> Notifications
            </Typography>
            <Typography className={classes.settingsSectionDescription}>
              Configure notification settings for form submissions
            </Typography>
          </Box>
          <IconButton size='small' className={classes.settingsExpandIcon}>
            {notificationsExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
        <Collapse in={notificationsExpanded}>
          <FormNotificationSettings
            formId={formId || ''}
            notificationConfig={form?.notificationConfig}
            onSaved={() => {
              onRefetchForms();
            }}
          />
        </Collapse>
      </Box>
    </Box>
  );
};
